import { useState } from "react"
import { Check, Image as ImageIcon, Maximize2, RotateCcw, UserPlus } from "lucide-react"
import { Link } from "@inertiajs/react"

import ImageLightbox from "./ImageLightbox"
import { PanelCard, PanelCardHead, PanelCardBody } from "./PanelCard"
import PanelEmpty from "./PanelEmpty"
import { Avatar } from "../Avatar"
import { Button } from "@/components/ui/button"
import { ORDER_MODAL_KEYS } from "./actions"

// Copy per server-derived illustration state (AQ-022). The panel never infers
// production from an empty spreads array — it reads order.illustration.state,
// which the presenter derives from the illustrator assignment + order phase.
const STATE_COPY = {
  awaitingPayment: {
    heading: "Payment is needed before work can begin",
    body: "Your illustrator is assigned, but illustration work will begin after this project is paid.",
  },
  awaitingContract: {
    heading: "Your illustrator is getting started",
    body: "Your illustrator has been selected and is finalizing their contract. Work begins after the contract is complete.",
  },
  assignedPreProduction: {
    heading: "Your illustrator is assigned",
    body: "Your illustrator has been selected. Work has not started yet.",
  },
  inProduction: {
    heading: "Illustrations in progress",
    body: "Your illustrator is drafting your spreads. Each one appears here for review the moment it's finished.",
  },
  awaitingReview: {
    heading: "Your first spreads are ready",
    body: "Your first spreads are ready for your review. Approve the set or request changes.",
  },
}

// Illustrations tab (illustration orders) — the real spreads an illustrator
// uploads (illustration_order.illustrations), rendered for review. Approve or
// request changes at the order level (the same CustomerReviewed flow covers
// illustration sets). Before any spread lands it shows a truthful state-driven
// message. Mirrors order-illustrations.html.
export default function IllustrationsPanel({ order, onOpenModal, onSelectTab }) {
  const spreads = order.deliverables?.illustrations ?? []
  const canApprove = order.permissions?.approveDesign
  const canRevise = order.permissions?.requestRevision
  const illus = order.illustration ?? {}
  const state = illus.state ?? (illus.illustratorAssigned ? "inProduction" : "unassigned")
  const assigned = Boolean(illus.illustratorAssigned)
  const illustrator = illus.illustrator ?? null
  const messageIllustrator = () => onSelectTab?.("chat")
  // A collaborator's read-only view (AU-44): the empty states keep their
  // message but drop the control, which leads to the owner's chat or support.
  const readOnly = Boolean(order.viewer?.readOnly)
  const [enlarged, setEnlarged] = useState(-1)

  if (!spreads.length) {
    // Unassigned: no illustrator yet, so nothing is in progress. Primary action
    // is Find Freelancers prefiltered for illustrators; no "Message" affordance.
    if (state === "unassigned") {
      return (
        <PanelEmpty
          Icon={UserPlus}
          title="Illustrations Preview"
          heading="Select your illustrator"
          body="An illustrator must be selected before work can begin. Browse illustrators and choose who you'd like to work with."
          action={
            !readOnly && (
              <Button asChild size="sm">
                <Link href="/find-freelancers?role=illustrator">Find illustrators</Link>
              </Button>
            )
          }
        />
      )
    }

    // Payment is a customer-action state, not contract finalization. Keep the
    // required action in the panel when Pay Now is currently allowed.
    if (state === "awaitingPayment") {
      const copy = STATE_COPY.awaitingPayment
      return (
        <PanelEmpty
          Icon={ImageIcon}
          title="Illustrations Preview"
          heading={copy.heading}
          body={copy.body}
          action={
            order.permissions?.payNow && (
              <Button size="sm" onClick={() => onOpenModal?.(ORDER_MODAL_KEYS.PAY_NOW)}>
                Pay now
              </Button>
            )
          }
        />
      )
    }

    // assignedOther: an illustrator is assigned but the order sits in a phase
    // outside the enumerated production/review/pre-production sets. Render the
    // neutral, phase-truthful status label with NO production claim.
    if (state === "assignedOther") {
      return (
        <PanelEmpty
          Icon={ImageIcon}
          title="Illustrations Preview"
          heading={illustrator?.name ? `${illustrator.name} is on your illustrations` : "Your illustrator is assigned"}
          body={illus.statusLabel ? `Status: ${illus.statusLabel}` : "Your illustrator is assigned to this project."}
          action={
            assigned && !readOnly && (
              <Button variant="outline" size="sm" onClick={messageIllustrator}>
                Message your illustrator
              </Button>
            )
          }
        />
      )
    }

    const copy = STATE_COPY[state] ?? STATE_COPY.inProduction
    return (
      <PanelEmpty
        Icon={ImageIcon}
        title="Illustrations Preview"
        heading={copy.heading}
        body={copy.body}
        action={
          assigned && !readOnly && (
            <Button variant="outline" size="sm" onClick={messageIllustrator}>
              Message your illustrator
            </Button>
          )
        }
      />
    )
  }

  return (
    <PanelCard>
      <PanelCardHead Icon={ImageIcon} title="Illustrations Preview">
        {illustrator && (
          <span className="flex items-center gap-2 text-[12.5px] text-ink-3">
            Illustrated by
            <Avatar initials={illustrator.initials} name={illustrator.name} size={22} />
            <b className="font-semibold text-foreground">{illustrator.name}</b>
          </span>
        )}
      </PanelCardHead>
      <PanelCardBody>
        <div className="mb-5 rounded-xl border border-border bg-accent px-4 py-3 text-[12.5px] leading-[1.55] text-muted-foreground">
          Review each spread as it&apos;s drafted. When the set looks right, approve to
          lock it in — or request changes and tell your illustrator what to adjust.
        </div>
        {/* 3 spreads across, 2 below 860, stacked below 560 */}
        <div className="grid grid-cols-1 gap-4 min-[561px]:grid-cols-2 min-[861px]:grid-cols-3">
          {spreads.map((art, i) => (
            <figure key={art.url || i} className="overflow-hidden rounded-2xl border border-border bg-card">
              <button
                type="button"
                aria-label={`Open Illustration ${i + 1} larger`}
                onClick={() => setEnlarged(i)}
                className="group relative block w-full cursor-zoom-in bg-secondary focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foreground"
              >
                <img src={art.url} alt={`Illustration ${i + 1}`} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                <span className="absolute bottom-3 right-3 grid size-[26px] place-items-center rounded-[7px] border border-border bg-card opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 max-[560px]:opacity-100">
                  <Maximize2 className="size-[14px] text-foreground" strokeWidth={2} />
                </span>
              </button>
              <figcaption className="px-4 py-3 text-[12.5px] font-semibold">
                Illustration {i + 1}
                {art.fileName && <span className="mt-px block truncate text-[11px] font-medium text-ink-3" title={art.fileName}>{art.fileName}</span>}
              </figcaption>
            </figure>
          ))}
        </div>

        <ImageLightbox
          wide
          title="Illustrations Preview"
          images={spreads.map((art, i) => ({ url: art.url, label: `Illustration ${i + 1}` }))}
          at={enlarged}
          onMove={setEnlarged}
          onClose={() => setEnlarged(-1)}
        />
      </PanelCardBody>

      {(canApprove || canRevise) && (
        <div className="flex flex-wrap items-center gap-4 border-t border-border bg-gradient-to-b from-card to-accent px-[22px] py-4 max-[720px]:gap-3 max-[720px]:px-4 max-[720px]:py-[14px]">
          <div className="max-[720px]:basis-full">
            <h4 className="font-display text-sm font-bold">Happy with your illustrations?</h4>
            <p className="mt-0.5 text-[12px] text-ink-3">Approving locks the set and asks your illustrator to prepare delivery files.</p>
          </div>
          <div className="ml-auto flex gap-[9px] max-[720px]:w-full">
            {canRevise && (
              <Button variant="outline" className="gap-[7px] max-[720px]:flex-1" onClick={() => onOpenModal?.("request-revision")}>
                <RotateCcw className="size-4" strokeWidth={2} />
                Request changes
              </Button>
            )}
            {canApprove && (
              <Button className="gap-[7px] font-bold max-[720px]:flex-1" onClick={() => onOpenModal?.("approve-design")}>
                <Check className="size-4" strokeWidth={2.2} />
                Approve illustrations
              </Button>
            )}
          </div>
        </div>
      )}
    </PanelCard>
  )
}
